import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { UserCircleIcon, CalendarIcon, CheckCircleIcon, TrendingUpIcon, DollarSignIcon, PhoneIcon, MailIcon, MapPinIcon, ShieldIcon, FileTextIcon, MessageSquareIcon, PackageIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Tabs } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Patient, Appointment, ClinicalHistory, FollowUpNote, Opportunity } from '../../types';
import { formatDate, formatTime, formatCurrency, getStatusName, getStageName, getStageColor } from '../../utils/helpers';
import { supabase } from '../../utils/supabase';
export function Patient360() {
  const {
    patients,
    appointments,
    clinicalHistories,
    followUpNotes,
    opportunities,
    getInsurance,
    getDoctor,
    getService,
    getSpecialty
  } = useData();
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [productSales, setProductSales] = useState<any[]>([]);
  const [isLoadingSales, setIsLoadingSales] = useState(false);
  // Filter active patients
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-patients-status');
      if (saved) setPatientStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  const activePatients = patients.filter(p => !patientStatuses[p.id]);
  const patientOptions = [{
    value: '',
    label: 'Seleccionar paciente...'
  }, ...activePatients.map(p => ({
    value: p.id,
    label: p.name
  }))];
  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  // Fetch product sales when patient changes
  useEffect(() => {
    if (selectedPatientId) {
      const fetchSales = async () => {
        setIsLoadingSales(true);
        try {
          const {
            data,
            error
          } = await supabase.from('product_sales').select('*').eq('patient_id', selectedPatientId).order('date', {
            ascending: false
          });
          if (!error && data) {
            setProductSales(data);
          }
        } catch (err) {
          logger.error('Error fetching product sales:', err);
        } finally {
          setIsLoadingSales(false);
        }
      };
      fetchSales();
    } else {
      setProductSales([]);
    }
  }, [selectedPatientId]);
  // Derived data
  const patientAppointments = appointments.filter(a => a.patientId === selectedPatientId);
  const patientHistories = clinicalHistories.filter(h => h.patientId === selectedPatientId);
  // For follow up notes, we match by contactName (since notes use contactName instead of patientId)
  // This is a best-effort match based on the current data structure
  const patientNotes = followUpNotes.filter(n => n.contactName === selectedPatient?.name);
  // Opportunities use contactId, which might not map 1:1 to patientId, but we'll try to match by name or if contactId matches
  const patientOpportunities = opportunities.filter(o => {
    // If we have a direct link (assuming contactId might sometimes hold patientId in this CRM setup)
    if (o.contactId === selectedPatientId) return true;
    // Otherwise, try to match by name (since contact and patient might be separate entities with same name)
    if (selectedPatient && o.name.toLowerCase().includes(selectedPatient.name.toLowerCase())) return true;
    return false;
  });
  // KPIs
  const totalAppointments = patientAppointments.length;
  const completedAppointments = patientAppointments.filter(a => a.status === 'completada').length;
  const today = new Date().toISOString().split('T')[0];
  const upcomingAppointments = patientAppointments.filter(a => a.status === 'programada' && a.date >= today).length;
  // Calculate total spent (completed appointments + product sales)
  const totalSpentAppointments = patientAppointments.filter(a => a.status === 'completada').reduce((sum, a) => {
    // Handle multiple services
    if (a.serviceIds && a.serviceIds.length > 0) {
      return sum + a.serviceIds.reduce((sSum, sId) => {
        const svc = getService(sId);
        if (!svc) return sSum;
        const discountPercent = Number(a.serviceDiscounts?.[sId] || 0);
        return sSum + Math.max(0, svc.price - svc.price * (discountPercent / 100));
      }, 0);
    }
    // Single service fallback
    const svc = getService(a.serviceId);
    if (!svc) return sum;
    const discountPercent = Number(a.serviceDiscounts?.[a.serviceId] || 0);
    return sum + Math.max(0, svc.price - svc.price * (discountPercent / 100));
  }, 0);
  const totalSpentProducts = productSales.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
  const totalSpent = totalSpentAppointments + totalSpentProducts;
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };
  return <div className="space-y-6" data-id="element-4280" data-mp="12fxokd2mz2kh">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 shadow-xl text-white" data-id="element-4281" data-mp="12ecrafoxt5ix">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(255,255,255,1) 19px, rgba(255,255,255,1) 20px)'
      }} data-id="element-4282" data-mp="1b76dyy6pr76t" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-4283" data-mp="nin62jrkswf9" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-4284" data-mp="sjb43pe82diz" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border-[40px] border-white/10 pointer-events-none" data-id="element-4285" data-mp="1yx0p9bbdwnoy" />
        <div className="absolute -bottom-10 right-32 w-40 h-40 rounded-full opacity-10 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)'
      }} data-id="element-4286" data-mp="23yzteney7omy" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-4287" data-mp="dstifwrkj4mq" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-4288" data-mp="dqh4duoxleey">
          <div data-id="element-4289" data-mp="3h9ae1wel9j3u">
            <div className="flex items-center gap-3 mb-2" data-id="element-4290" data-mp="3h90jdvilmnrt">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-4291" data-mp="3h7h1y1l7i6og">
                <UserCircleIcon className="w-6 h-6 text-white" data-id="element-4292" data-mp="3c8mbqekwtfub" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-4293" data-mp="3r0o8vqvlhuqn">Cliente 360</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-4294" data-mp="3m7bvye0a3cdq">
              Vista unificada del paciente
            </p>
          </div>
        </div>
      </div>

      {/* Selector */}
      <Card data-id="element-4295" data-mp="2tjg17eu9rujl">
        <div className="max-w-md" data-id="element-4296" data-mp="2thv7frfs4fms">
          <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={setSelectedPatientId} searchable placeholder="Buscar paciente..." data-id="element-4297" data-mp="1926unv5fuz1q" />
          
        </div>
      </Card>

      {!selectedPatient ? <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-4298" data-mp="102jsh74vxp7m">
          <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6" data-id="element-4299" data-mp="1422anv5bnm6r">
            <UserCircleIcon className="w-10 h-10 text-gray-400" data-id="element-4300" data-mp="1rtpu5s097uz0" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-4301" data-mp="1wqgc6kvmd0n2">
            Selecciona un paciente
          </h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-4302" data-mp="1irgq9kzhqzul">
            Busca y selecciona un paciente en el menú desplegable para ver toda
            su información unificada en esta pantalla.
          </p>
        </div> : <div className="space-y-6" data-id="element-4303" data-mp="cbo4ym7gpg66">
          {/* Patient Profile Card */}
          <Card className="border-t-4 border-t-teal-500 shadow-md overflow-hidden" data-id="element-4304" data-mp="ccz5md5m27t2">
            <div className="flex flex-col md:flex-row gap-6 items-start" data-id="element-4305" data-mp="133av65br3o97">
              <div className="flex-shrink-0" data-id="element-4306" data-mp="yz35a4tk429e">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg" data-id="element-4307" data-mp="z28s1depqs1y">
                  {getInitials(selectedPatient.name)}
                </div>
              </div>

              <div className="flex-1 min-w-0" data-id="element-4308" data-mp="1moff96wpkecr">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4" data-id="element-4309" data-mp="1hqno4f3w0w9m">
                  <div data-id="element-4310" data-mp="1cznov37aq23y">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1" data-id="element-4311" data-mp="1cz5iveuu09iu">
                      {selectedPatient.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-4312" data-mp="17yzwoe7bzf85">
                      <span className="capitalize" data-id="element-4313" data-mp="20proz0l9zjq7">
                        {selectedPatient.gender}
                      </span>
                      <span data-id="element-4314" data-mp="281plfoclvpzj">•</span>
                      <span data-id="element-4315" data-mp="282r2c9gaz6p6">{selectedPatient.age} años</span>
                      {selectedPatient.birthDate && <>
                          <span data-id="element-4316" data-mp="2hyu1r7jrsr8f">•</span>
                          <span data-id="element-4317" data-mp="2hxsrv6ehqasa">Nacimiento: {selectedPatient.birthDate}</span>
                        </>}
                      {selectedPatient.cedula && <>
                          <span data-id="element-4318" data-mp="2qziiia0jt5p0">•</span>
                          <span data-id="element-4319" data-mp="2qyh0tmp11kyb">Cédula: {selectedPatient.cedula}</span>
                        </>}
                    </div>
                  </div>

                  {selectedPatient.insuranceId && <Badge variant="info" className="flex items-center gap-1" data-id="element-4320" data-mp="30osa7orhcmcl">
                      <ShieldIcon className="w-3 h-3" data-id="element-4321" data-mp="35oeu2h1td0ud" />
                      {getInsurance(selectedPatient.insuranceId)?.name || 'Seguro'}
                    </Badge>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-id="element-4322" data-mp="3aloj3vlajaho">
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-4323" data-mp="3emqkoj2ojymt">
                    <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-4324" data-mp="mniv1hry97ad" />
                    <span data-id="element-4325" data-mp="rozsf3b86bm7">{selectedPatient.phone || 'Sin teléfono'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-4326" data-mp="wlylrjn2eejc">
                    <MailIcon className="w-4 h-4 text-gray-400" data-id="element-4327" data-mp="11fugmd817laa" />
                    <span className="truncate" data-id="element-4328" data-mp="16e4c6oxo4nwf">
                      {selectedPatient.email || 'Sin email'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-4329" data-mp="1agsml12162vi">
                    <MapPinIcon className="w-4 h-4 text-gray-400" data-id="element-4330" data-mp="1fc7yhngh9opa" />
                    <span className="truncate" data-id="element-4331" data-mp="1kclwecod7i9l">
                      {selectedPatient.address || 'Sin dirección'}
                    </span>
                  </div>
                </div>

                {selectedPatient.emergencyContact?.name && <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 inline-block" data-id="element-4332" data-mp="3m6bq9yuid0s7">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-4333" data-mp="39tzj1la6z6h5">
                      Contacto de Emergencia
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-4334" data-mp="2zw3dra3xr5qy">
                      {selectedPatient.emergencyContact.name} (
                      {selectedPatient.emergencyContact.relationship})
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4335" data-mp="3sm26bfhc9l0d">
                      {selectedPatient.emergencyContact.phone}
                    </p>
                  </div>}
              </div>
            </div>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4336" data-mp="2c70k13w11mg3">
            <Card padding="sm" className="bg-teal-50 dark:bg-teal-900/20 border-teal-100 dark:border-teal-800/50" data-id="element-4337" data-mp="2c4egdfc9br35">
            
              <div className="flex items-center gap-3" data-id="element-4338" data-mp="z88jist9w1ej">
                <div className="p-3 bg-teal-100 dark:bg-teal-800/50 rounded-lg text-teal-600 dark:text-teal-400" data-id="element-4339" data-mp="z8qj9zf58xr7">
                  <CalendarIcon className="w-6 h-6 text-white" data-id="element-4340" data-mp="1377v2w9iewey" />
                </div>
                <div data-id="element-4341" data-mp="p9j5jb4hz13k">
                  <p className="text-sm font-medium text-teal-600 dark:text-teal-400" data-id="element-4342" data-mp="u8vv43mr6m9u">
                    Total Citas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4343" data-mp="fiwyi5aujiwh">
                    {totalAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800/50" data-id="element-4344" data-mp="6kl03h3qngth">
            
              <div className="flex items-center gap-3" data-id="element-4345" data-mp="bg0au06g8wlo">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg text-emerald-600 dark:text-emerald-400" data-id="element-4346" data-mp="3srt0e4zswxin">
                  <CheckCircleIcon className="w-6 h-6 text-white" data-id="element-4347" data-mp="1jf8xnpq81m2" />
                </div>
                <div data-id="element-4348" data-mp="3e4b1jn8x6x45">
                  <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400" data-id="element-4349" data-mp="3e5m79slxeexw">
                    Completadas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4350" data-mp="3o30c0uyhlfad">
                    {completedAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-amber-50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/50" data-id="element-4351" data-mp="w7sdkm3a7h4">
            
              <div className="flex items-center gap-3" data-id="element-4352" data-mp="3syz7t3m59zkt">
                <div className="p-3 bg-amber-100 dark:bg-amber-800/50 rounded-lg text-amber-600 dark:text-amber-400" data-id="element-4353" data-mp="am10gbn0w4gm">
                  <TrendingUpIcon className="w-6 h-6 text-white" data-id="element-4354" data-mp="5oj21f6mt7mv" />
                </div>
                <div data-id="element-4355" data-mp="km1o29rxd6qm">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400" data-id="element-4356" data-mp="kli3elaw448w">
                    Próximas
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4357" data-mp="2955senzh9xhi">
                    {upcomingAppointments}
                  </p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-800/50" data-id="element-4358" data-mp="2iu5w2koxdh21">
            
              <div className="flex items-center gap-3" data-id="element-4359" data-mp="2nt91454wnna0">
                <div className="p-3 bg-purple-100 dark:bg-purple-800/50 rounded-lg text-purple-600 dark:text-purple-400" data-id="element-4360" data-mp="1q6a3ns6e2bb2">
                  <DollarSignIcon className="w-6 h-6 text-white" data-id="element-4361" data-mp="1v6663mzftuoi" />
                </div>
                <div data-id="element-4362" data-mp="203y859tsqx6f">
                  <p className="text-sm font-medium text-purple-600 dark:text-purple-400" data-id="element-4363" data-mp="203es5ynpiiv1">
                    Total Gastado
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-4364" data-mp="3bjd3682g3eyw">
                    {formatCurrency(totalSpent)}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs tabs={[{
        id: 'citas',
        label: `Citas (${patientAppointments.length})`,
        icon: <CalendarIcon className="w-4 h-4" data-id="element-4366" data-mp="zwje0tu8l9hj" />,
        content: <Card padding="none" data-id="element-4367" data-mp="v51d25s7r8wk">
                    <div className="overflow-x-auto" data-id="element-4368" data-mp="v3zxbo095y8i">
                      <table className="w-full" data-id="element-4369" data-mp="q6hn7uai1cyx">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-4370" data-mp="q5opfitipxsx">
                          <tr data-id="element-4371" data-mp="l50s6fep4kq9">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4372" data-mp="h50c6qby81h3">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4373" data-mp="77c4kzt8uzhi">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4374" data-mp="26idium7ja1ld">
                              Servicio
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4375" data-mp="3w00lcpkoygkw">
                              Estado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4376" data-mp="3r28y48uzrin1">
                          {patientAppointments.length === 0 ? <tr data-id="element-4377" data-mp="imfdbhtmnl06">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-4378" data-mp="ingr3u212lll">
                        
                                No hay citas registradas
                              </td>
                            </tr> : patientAppointments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(apt => {
                  const doctor = getDoctor(apt.doctorId);
                  const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId];
                  const serviceNames = serviceIds.map(id => getService(id)?.name).filter(Boolean).join(', ');
                  return <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-4379" data-mp="1bh3tkljf9egh">
                          
                                    <td className="py-3 px-4 text-sm" data-id="element-4380" data-mp="1pfv3rwv5yfzc">
                                      <div className="font-medium text-gray-900 dark:text-white" data-id="element-4381" data-mp="1lfkpzy14ql9x">
                                        {formatDate(apt.date)}
                                      </div>
                                      <div className="text-gray-500 text-xs" data-id="element-4382" data-mp="sr5ma7ng7pdw">
                                        {formatTime(apt.time)}
                                      </div>
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-4383" data-mp="11q93t76yvwqa">
                                      {doctor ? `Dr. ${doctor.name}` : '-'}
                                    </td>
                                    <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-4384" data-mp="9zw0aywhr85f">
                                      {serviceNames || '-'}
                                    </td>
                                    <td className="py-3 px-4" data-id="element-4385" data-mp="13u9xfn9befoe">
                                      <Badge variant={apt.status === 'completada' ? 'success' : apt.status === 'cancelada' ? 'danger' : apt.status === 'reprogramada' ? 'warning' : 'info'} data-id="element-4386" data-mp="17uk3ezx3x639">
                              
                                        {getStatusName(apt.status)}
                                      </Badge>
                                    </td>
                                  </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'historial',
        label: `Historial Clínico (${patientHistories.length})`,
        icon: <FileTextIcon className="w-4 h-4" data-id="element-4387" data-mp="20m3sh0gdxed6" />,
        content: <Card padding="none" data-id="element-4388" data-mp="25iu3vah8vls9">
                    <div className="overflow-x-auto" data-id="element-4389" data-mp="25jn55vxfpj30">
                      <table className="w-full" data-id="element-4390" data-mp="2fntun0lyir0l">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-4391" data-mp="2folq8bfk0oqv">
                          <tr data-id="element-4392" data-mp="25p4kxv47scvf">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4393" data-mp="25ocpckamaf55">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4394" data-mp="2ydiik9re576g">
                              Doctor
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4395" data-mp="2th1mmtdy9oit">
                              Motivo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4396" data-mp="2km559dk2t5jv">
                              Diagnóstico
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4397" data-mp="16tizel0ff0bp">
                          {patientHistories.length === 0 ? <tr data-id="element-4398" data-mp="12qml36hx5qdk">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-4399" data-mp="12tikkz9f0n44">
                        
                                No hay historiales registrados
                              </td>
                            </tr> : patientHistories.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(h => <tr key={h.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-4400" data-mp="1lhx7bacp9ez4">
                      
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-4401" data-mp="1gqf6qnel9geo">
                                    {formatDate(h.date.toISOString().split('T')[0])}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-300" data-id="element-4402" data-mp="298sx61xli03q">
                                    {h.doctorName || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-4403" data-mp="2btqx3ljpxqb3">
                                    {h.personalBackground?.consultationReason || '-'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[200px]" data-id="element-4404" data-mp="2qpq22nbj7ac0">
                                    {h.diagnosis || '-'}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'notas',
        label: `Notas (${patientNotes.length})`,
        icon: <MessageSquareIcon className="w-4 h-4" data-id="element-4405" data-mp="39f52cfje7vfc" />,
        content: <div className="space-y-4" data-id="element-4406" data-mp="3efazgb8fma4t">
                    {patientNotes.length === 0 ? <Card data-id="element-4407" data-mp="3ecyahe49azi9">
                        <div className="py-8 text-center text-gray-500" data-id="element-4408" data-mp="3ifurx1i28pkm">
                          No hay notas de seguimiento
                        </div>
                      </Card> : patientNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(note => <Card key={note.id} className="bg-gray-50 dark:bg-gray-800/30" data-id="element-4409" data-mp="1s3wq5cludied">
                
                            <div className="flex justify-between items-start mb-2" data-id="element-4410" data-mp="1ndgbxqa75sqy">
                              <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-4411" data-mp="1nbde4k2syvbo">
                                {note.subject}
                              </h3>
                              <span className="text-xs text-gray-500" data-id="element-4412" data-mp="1x2rhx41seham">
                                {new Date(note.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap" data-id="element-4413" data-mp="26zcohttgg0gk">
                              {note.description}
                            </p>
                            <div className="mt-3 text-xs text-gray-500" data-id="element-4414" data-mp="cp90l7abwypa">
                              Por: {note.userName}
                            </div>
                          </Card>)}
                  </div>
      }, {
        id: 'oportunidades',
        label: `Oportunidades (${patientOpportunities.length})`,
        icon: <TrendingUpIcon className="w-4 h-4" data-id="element-4415" data-mp="3owziq1vpsuh0" />,
        content: <Card padding="none" data-id="element-4416" data-mp="3twlx4ena4gre">
                    <div className="overflow-x-auto" data-id="element-4417" data-mp="3tvk8169fhtqo">
                      <table className="w-full" data-id="element-4418" data-mp="3ezl6kegtqrui">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-4419" data-mp="3f2z3h76py8uk">
                          <tr data-id="element-4420" data-mp="360hisx6ppo9o">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4421" data-mp="3619ee80b7lzy">
                              Nombre
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4422" data-mp="148big3b8kdas">
                              Tipo
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4423" data-mp="35akfy0pz4b6x">
                              Etapa
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4424" data-mp="309wo5ctx7qbn">
                              Monto
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4425" data-mp="2md02dpsnsz1m">
                          {patientOpportunities.length === 0 ? <tr data-id="element-4426" data-mp="2hjx6hb0etdi9">
                              <td colSpan={4} className="py-8 text-center text-gray-500" data-id="element-4427" data-mp="2cm54fo61wb0c">
                        
                                No hay oportunidades registradas
                              </td>
                            </tr> : patientOpportunities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(opp => <tr key={opp.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-4428" data-mp="1ynf5s43l239v">
                      
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-4429" data-mp="e41ygbc5govy">
                                    {opp.name}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 capitalize" data-id="element-4430" data-mp="n5t27an4qln6">
                                    {opp.opportunityType.replace(/_/g, ' ')}
                                  </td>
                                  <td className="py-3 px-4" data-id="element-4431" data-mp="3vkt8gejamphc">
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(opp.stage)}`} data-id="element-4432" data-mp="3vlcqdv8y1dvd">
                          
                                      {getStageName(opp.stage)}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-gray-900 dark:text-white" data-id="element-4433" data-mp="37ted55pxp8fl">
                                    {formatCurrency(opp.amount)}
                                  </td>
                                </tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }, {
        id: 'productos',
        label: `Productos (${productSales.length})`,
        icon: <PackageIcon className="w-4 h-4" data-id="element-4434" data-mp="ormoc3njgozz" />,
        content: <Card padding="none" data-id="element-4435" data-mp="10zcx8eezh8o">
                    <div className="overflow-x-auto" data-id="element-4436" data-mp="120pjhb2wbsi">
                      <table className="w-full" data-id="element-4437" data-mp="12tx2khv35bp">
                        <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-4438" data-mp="3s8lnlkzjwjz1">
                          <tr data-id="element-4439" data-mp="a2yv44160rd7">
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4440" data-mp="52kmajrqpde4">
                              Fecha
                            </th>
                            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4441" data-mp="1lix98mb7a3la">
                              Productos
                            </th>
                            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase" data-id="element-4442" data-mp="1oxgmqqp1snpf">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-4443" data-mp="1jzf426vjaqeh">
                          {isLoadingSales ? <tr data-id="element-4444" data-mp="27opzr9y11ulr">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-4445" data-mp="27prm4akhu3is">
                        
                                Cargando ventas...
                              </td>
                            </tr> : productSales.length === 0 ? <tr data-id="element-4446" data-mp="1xxltcymf3i2j">
                              <td colSpan={3} className="py-8 text-center text-gray-500" data-id="element-4447" data-mp="22xrfy0dtyedr">
                        
                                No hay compras de productos
                              </td>
                            </tr> : productSales.map(sale => {
                  const items = Array.isArray(sale.items) ? sale.items : [];
                  const itemsDesc = items.map((i: any) => `${i.productName || 'Producto'} (x${i.quantity})`).join(', ');
                  return <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-4448" data-mp="lmrf2kwr6ylv">
                          
                                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white" data-id="element-4449" data-mp="hlz67srsmvq4">
                                    {formatDate(sale.date)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-4450" data-mp="19df26df8put0">
                                    {itemsDesc || 'Venta de productos'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-right font-medium text-emerald-600 dark:text-emerald-400" data-id="element-4451" data-mp="10b5rnnjz21fm">
                                    {formatCurrency(sale.total)}
                                  </td>
                                </tr>;
                })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
      }]} data-id="element-4365" data-mp="14tt142u5l927" />
        
        </div>}
    </div>;
}